import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import CloseIcon from 'components/ReusableComponents/CloseIcon'
import Dialog from 'components/ReusableComponents/Dialog'
import {
  EmailEditorProvider,
  useEmailEditor
} from 'features/EmailEditor/EmailEditorContext'
import EmailEditorPanel from 'features/EmailEditor/EmailEditorPanel'
import EmailPreview from 'features/EmailEditor/EmailPreview'
import TestEmail from 'features/EmailEditor/TestEmail'
import PropTypes from 'prop-types'
import { useState } from 'react'

import ConfigurationStep from './ConfigurationStep'
import styles from './EditStepDialog.module.scss'

const steps = {
  configuration: 'configuration',
  email: 'email'
}

const stepNames = {
  configuration: 'Configuration',
  email: 'Email'
}

function EditStepDialogInner({
  title,
  onClose,
  onSave,
  isSaving,
  initialData,
  timingKind,
  timingLabel,
  contentId
}) {
  const [activeStep, setActiveStep] = useState(steps.configuration)
  const [isEnabled, setIsEnabled] = useState(initialData?.isEnabled ?? true)
  const [daysBeforeCheckIn, setDaysBeforeCheckIn] = useState(
    initialData?.daysBeforeCheckIn ?? 3
  )

  const { refreshEmailData, saveChanges, hasUnsavedChanges } = useEmailEditor()

  const isEditingEmail = activeStep === steps.email && Boolean(contentId)

  const handleSave = async () => {
    if (hasUnsavedChanges) {
      await saveChanges()
    }
    onSave?.({
      isEnabled,
      daysBeforeCheckIn
    })
  }

  return (
    <Dialog verticalPadding={30}>
      <div className={styles.resolutionWrapper}>
        <div className={styles.dialogWrapper}>
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            <button className={styles.closeButton} onClick={onClose} type="button">
              <CloseIcon />
            </button>
            <div className={styles.tabs}>
              {Object.keys(steps).map(step => (
                <button
                  key={step}
                  onClick={() => setActiveStep(step)}
                  className={step === activeStep ? styles.activeTab : styles.tab}
                  type="button"
                >
                  {stepNames[step]}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.content}>
            {activeStep === steps.configuration && (
              <ConfigurationStep
                isEnabled={isEnabled}
                setIsEnabled={setIsEnabled}
                timingKind={timingKind}
                timingLabel={timingLabel}
                daysBeforeCheckIn={daysBeforeCheckIn}
                setDaysBeforeCheckIn={setDaysBeforeCheckIn}
              />
            )}

            {activeStep === steps.email && (
              <div className={styles.emailStepContent}>
                {contentId ? (
                  <EmailEditorPanel
                    embedded
                    hideFooter
                    onClose={refreshEmailData}
                  />
                ) : (
                  <p className={styles.emailUnavailable}>
                    Email content for this hotel is still being created. Close this dialog,
                    wait a moment for the page to finish loading, then open Edit email again.
                    If this persists, refresh the OTA Buster page so campaign content can be
                    initialized for this property.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <SecondaryRegularButton onClick={onClose} disabled={isSaving}>
              Cancel
            </SecondaryRegularButton>
            <PrimaryRegularButton onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </PrimaryRegularButton>
          </div>
        </div>

        {isEditingEmail && (
          <div className={`${styles.previewHolder} ${styles.previewHolderEmail}`}>
            <EmailPreview
              isEditable={false}
              holderClassName={styles.emailPreviewHolder}
              testHolderClassName={styles.emailTestHolder}
              skipInitialSectionReset
              hideEditContentButton
            >
              <TestEmail />
            </EmailPreview>
          </div>
        )}
      </div>
    </Dialog>
  )
}

EditStepDialogInner.propTypes = {
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  contentId: PropTypes.number,
  initialData: PropTypes.shape({
    isEnabled: PropTypes.bool,
    daysBeforeCheckIn: PropTypes.number
  }),
  timingKind: PropTypes.oneOf(['event', 'daysBeforeCheckIn']).isRequired,
  timingLabel: PropTypes.string.isRequired
}

function EditStepDialog({ contentId, contentCategory, ...props }) {
  return (
    <EmailEditorProvider contentId={contentId} contentCategory={contentCategory}>
      <EditStepDialogInner contentId={contentId} {...props} />
    </EmailEditorProvider>
  )
}

EditStepDialog.propTypes = {
  contentId: PropTypes.number,
  contentCategory: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  initialData: PropTypes.shape({
    isEnabled: PropTypes.bool,
    daysBeforeCheckIn: PropTypes.number
  }),
  timingKind: PropTypes.oneOf(['event', 'daysBeforeCheckIn']).isRequired,
  timingLabel: PropTypes.string.isRequired
}

export default EditStepDialog
